import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Chip, Divider, Stack, Typography, Skeleton, Link} from 'views/components';
import {HoverToolBox, Translate, translate} from 'views/module';

import {FEEDBACK_STATUS, FEEDBACK_OPTIONS} from './utils/constants';

import backend from 'utils/backend';

const CaregiverView = (props) => {
  const [caregiverData, setCaregiverData] = React.useState(null);

  const [toolbarVisible, setToolbarVisible] = React.useState(false);
  const [mouseOver, setMouseOver] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isFeedbackLoading, setIsFeedbackLoading] = React.useState(false);
  const [feedbackStatusKey, setFeedbackStatusKey] = React.useState(FEEDBACK_STATUS.NOT_SUBMITTED.key);
  const [feedbackDetails, setFeedbackDetails] = React.useState(null);
  const [feedbackProviderName, setFeedbackProviderName] = React.useState('Unknown Name');
  const [feedbackProviderConnectionType, setFeedbackProviderConnectionType] = React.useState('Unknown relationship type');

  const caregiver = props.caregiver;
  const progressWrap = props.statusProgressWrap;
  const clientDetail = props.clientData;
  const {
    clientRn,
    relationships,
  } = clientDetail;

  const {
    caregiverRn,
    status = 'ACTIVE',
  } = caregiver;
  const removeCaregiver = props.removeCaregiver;

  React.useEffect(() => {
    setToolbarVisible(mouseOver);
  }, [mouseOver]);

  // Load caregiver data
  React.useEffect(() => {
    backend.caregiver.getCaregiver(caregiverRn, true)
        .then((e) => {
          setCaregiverData(e);
        })
        .catch(() => {
          console.error('Failed to load caregiver data');
        });
  }, [caregiver]);

  React.useEffect(() => {
    if (status === 'REVIEW') {
      setIsFeedbackLoading(true);
      progressWrap(async () => {
        const result = await backend.clientSupport.getCaregiverFeedback(clientRn, caregiver.caregiverRn);
        const feedback = result.feedback;
        if (feedback) {
          setFeedbackDetails(feedback);
          const feedbackProvider = relationships?.[result.clientRelationshipRn];
          setFeedbackProviderName(feedbackProvider?.name?.trim() || 'Unknown Name');
          setFeedbackProviderConnectionType(feedbackProvider?.connectionType?.trim() || 'Unknown relationship type');
          if (feedback.isMatch) {
            setFeedbackStatusKey(FEEDBACK_STATUS.SUBMITTED_WANTS_MEETING.key);
          } else {
            setFeedbackStatusKey(FEEDBACK_STATUS.SUBMITTED_NO_MATCH.key);
          }
        }
        setIsFeedbackLoading(false);
      });
    }
  }, [caregiver]);

  return (
    <Box>
      <Divider/>
      <Box
        onMouseOver={(e) => setMouseOver(true) & setAnchorEl(e.currentTarget)}
        onMouseLeave={(e) => setMouseOver(false)}
        sx={{
          backgroundColor: toolbarVisible ? 'background.highlighted': 'inherit',
          p: 2,
        }}
      >
        <Stack spacing={1}>
          <Stack
            direction={{xs: 'column', sm: 'row'}}
            alignItems={{xs: 'flex-start', sm: 'center'}}
            justifyContent="space-between"
            spacing={1}
            sx={{
              pr: {xs: 0, sm: 5},
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Chip
                label={
                  translate(`client.ClientDetail.CaregiverConnections.AddCaregiver.Status.${status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}`)
                }
                sx={{
                  backgroundColor: status === 'ACTIVE' ? '#FFE0CC' : '#FFD54F',
                  color: '#000',
                }}
              />
              <Stack>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  <Translate expression='client.ClientDetail.CaregiverConnections.Caregiver.CaregiverName'/>
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                  }}
                >
                  {caregiverData ? (
                <Link href={`/caregiver/${caregiverData.caregiverRn}`}>
                  {caregiverData.caregiverDetail.caregiverName}
                </Link>
              ) : (
                <Skeleton sx={{fontSize: '14px', width: '100px'}} />
              )}
                </Typography>
              </Stack>
            </Stack>

            {
              status === 'REVIEW' && (
                <>
                  {
                    isFeedbackLoading &&
                  <Skeleton variant="rectangular" width={120} height={36} sx={{borderRadius: 1}} />
                  }
                  {
                    !isFeedbackLoading &&
                  <Chip
                    variant="outlined"
                    icon={FEEDBACK_STATUS[feedbackStatusKey].icon}
                    label={translate(FEEDBACK_STATUS[feedbackStatusKey].translationKey)}
                    sx={{
                      'color': FEEDBACK_STATUS[feedbackStatusKey].colorDark,
                      'bgcolor': FEEDBACK_STATUS[feedbackStatusKey].colorLight,
                      'borderColor': FEEDBACK_STATUS[feedbackStatusKey].colorDark,
                    }}
                  />
                  }
                </>
              )
            }
          </Stack>

          {
            feedbackDetails && (
              <Stack>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                  }}
                >
                  <Translate expression='client.ClientDetail.CaregiverConnections.Caregiver.Feedback.DetailsTitle'/>
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    <Translate expression='client.ClientDetail.CaregiverConnections.Caregiver.Feedback.ProviderName'/>
                  </Typography>
                  <Typography
                    variant="body2"
                  >
                    {feedbackProviderName}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    <Translate expression='client.ClientDetail.CaregiverConnections.Caregiver.Feedback.ProviderConnectionType'/>
                  </Typography>
                  <Typography
                    variant="body2"
                  >
                    {feedbackProviderConnectionType}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    <Translate expression='client.ClientDetail.CaregiverConnections.Caregiver.Feedback.SelectedOptionsTitle'/>
                  </Typography>
                  <Typography
                    variant="body2"
                  >
                    {
                      feedbackDetails.selectedOptions.length === 0 ?
                      translate('client.ClientDetail.CaregiverConnections.Caregiver.Feedback.NoneProvided') :
                      feedbackDetails.selectedOptions.map((option) => (
                        translate(FEEDBACK_OPTIONS[option].translationKey)
                      )).join(', ')
                    }
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 500,
                    }}
                  >
                    <Translate expression='client.ClientDetail.CaregiverConnections.Caregiver.Feedback.AdditionalCommentsTitle'/>
                  </Typography>
                  <Typography
                    variant="body2"
                  >
                    {
                      !feedbackDetails.additionalComments.trim() ?
                      translate('client.ClientDetail.CaregiverConnections.Caregiver.Feedback.NoneProvided') :
                      feedbackDetails.additionalComments
                    }
                  </Typography>
                </Stack>
              </Stack>
            )
          }
        </Stack>

        <HoverToolBox
          toolBar={toolbarVisible}
          anchorEl={anchorEl}
          onClickDelete={() => removeCaregiver(caregiverRn)}
          hideTool={{
            emoji: true,
            copy: true,
            edit: true,
            delete: false,
          }}
        />
      </Box>
    </Box>
  );
};

CaregiverView.propTypes = {
  caregiver: PropTypes.object.isRequired,
  removeCaregiver: PropTypes.func.isRequired,
  clientData: PropTypes.object.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(CaregiverView));
